import type { SettingsTemplateType } from '@/shared/generated'

import React from 'react'
import { CheckIcon, Undo2Icon } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'

import { SettingsTemplateActions } from '@/entities/settings-template'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import {
	Modal,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'
import { useMutationObservers } from '@/shared/lib/useMutationObservers'

import SettingsTemplateProvider, {
	useSettingsTemplateContext,
} from './SettingsTemplateProvider'
import SettingsTemplateResolver from './SettingsTemplateResolver'

export default function SettingsTemplateModalWrapped({
	isOpen,
	close,
	id,
	template_type,
}: {
	isOpen: boolean
	close: () => void
	id?: string
	template_type: SettingsTemplateType
}) {
	useMutationObservers({
		mutationKey: ['settings-templates-delete'],
		onSuccess: () => close(),
	})

	return (
		<SettingsTemplateProvider
			id={id}
			template_type={template_type}
			onFinish={close}
		>
			<SettingsTemplateModal isOpen={isOpen} close={close} />
		</SettingsTemplateProvider>
	)
}
const SettingsTemplateModal = ({
	isOpen,
	close,
}: {
	isOpen: boolean
	close: () => void
}) => {
	const { t } = useTranslation()

	const context = useSettingsTemplateContext()
	const handleOpenChange = async (open: boolean) => {
		if (context.methods.formState.isDirty && !open) {
			const answer = await confirm({
				title: t('navigation.blocked.pc_settings.title'),
				description: <Trans i18nKey='navigation.blocked.pc_settings.message' />,
				actionText: t('navigation.blocked.pc_settings.ok'),
				cancelText: t('navigation.blocked.pc_settings.cancel'),
			})
			if (answer) {
				close()
			}
		} else if (!open) {
			close()
		}
	}

	return (
		<Modal open={isOpen} onOpenChange={handleOpenChange}>
			<ModalContent
				className='h-[90vh] max-w-5xl p-0'
				data-testid='settings-template-modal'
			>
				<ErrorBoundary>
					<SettingTemplateForm />
				</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}

function SettingTemplateForm() {
	const { t } = useTranslation()

	const context = useSettingsTemplateContext()

	return (
		<FormProvider
			methods={context!.methods}
			onSubmit={context.handleSubmit}
			className='flex h-full flex-col overflow-hidden p-4'
		>
			<ModalHeader className='pr-6'>
				<div className='flex w-full items-start justify-between'>
					<ModalTitle>
						{`${t('entity.template')} - ${t(`template.type.${context.template_type}`)}`}
					</ModalTitle>
					<div className='flex items-center gap-2'>
						{context.template && (
							<SettingsTemplateActions template={context.template} />
						)}
						<Separator orientation='vertical' className='mr-2 h-6!' />
					</div>
				</div>
			</ModalHeader>
			<Blanket
				className='absolute flex flex-col items-center justify-center rounded-lg bg-white/50 backdrop-blur-lg dark:bg-black/50'
				isTinted={context.isPending || context.isLoading}
			>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<Spinner className='opacity-50' />
						</EmptyMedia>
						{context.isPending && (
							<EmptyDescription>
								{t('template_pending_hint', {
									defaultValue: 'Saving template...',
								})}
							</EmptyDescription>
						)}
					</EmptyHeader>
				</Empty>
			</Blanket>
			<SettingsTemplateResolver />
			<ModalFooter className='pt-4'>
				<ModalClose asChild>
					<Button
						className='mr-auto'
						data-testid='settings-template-modal-cancel'
					>
						{t('general_cancel')}
					</Button>
				</ModalClose>
				<div className='flex items-center gap-2'>
					{(context.methods.formState?.isDirty || context.isNew) && (
						<div className='flex items-center space-x-2'>
							{!context.isNew && (
								<Button
									data-testid='settings-template-form-revert-changes'
									onClick={context.handleReset}
									variant='ghost'
								>
									<Undo2Icon />
									{t('app.revert_changes')}
								</Button>
							)}

							<Button
								variant='primary'
								data-testid='settings-template-form-save'
								disabled={context.isPending || context.isLoading}
								onClick={context.handleSubmit}
							>
								{context.isPending ? <Spinner /> : <CheckIcon />}
								{t('general_save')}
							</Button>
						</div>
					)}
				</div>
			</ModalFooter>
		</FormProvider>
	)
}
